import { FC, ReactNode } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Skeleton from "@mui/material/Skeleton";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";

type ColorType =
  | "primary"
  | "secondary"
  | "info"
  | "success"
  | "warning"
  | "error"
  | "light"
  | "dark";

interface PercentageInfo {
  color?: ColorType | "white";
  amount?: string | number;
  label?: string;
}

interface ComplexStatisticsCardProps {
  color?: ColorType;
  title: string;
  count?: string | number;
  percentage?: PercentageInfo;
  icon: ReactNode;
  loading?: boolean;
  selected?: boolean;
  showExpanded?: boolean;
  dataComponent?: React.ComponentType<any>;
  onClick?: () => void;
}

const ComplexStatisticsCard: FC<ComplexStatisticsCardProps> = ({
  color = "info",
  title,
  count,
  percentage,
  icon,
  loading = false,
  selected = false,
  showExpanded = false,
  onClick,
}) => (
  <Card
    onClick={onClick}
    sx={{
      height: "100%",
      p: 2.5,
      cursor: onClick ? "pointer" : "default",
      borderColor: selected ? "info.main" : "divider",
      boxShadow: selected ? "0 0 0 3px rgba(37, 99, 235, 0.10)" : undefined,
      transition: "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
      "&:hover": onClick
        ? {
            transform: "translateY(-2px)",
            borderColor: "#bfdbfe",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
          }
        : undefined,
    }}
  >
    <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.25}>
      <MDBox
        width={42}
        height={42}
        borderRadius="lg"
        display="grid"
        sx={{
          placeItems: "center",
          color: `${color}.main`,
          bgcolor: color === "dark" ? "grey.100" : `${color}.50`,
          backgroundColor:
            color === "info"
              ? "#eff6ff"
              : color === "success"
              ? "#f0fdf4"
              : color === "warning"
              ? "#fffbeb"
              : color === "error"
              ? "#fef2f2"
              : "#f1f5f9",
        }}
      >
        <Icon fontSize="small" color="inherit">
          {icon}
        </Icon>
      </MDBox>
      {onClick && (
        <Icon sx={{ color: "text.secondary", fontSize: 20 }}>
          {selected || showExpanded ? "expand_less" : "north_east"}
        </Icon>
      )}
    </MDBox>

    <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
      {title}
    </MDTypography>
    {loading ? (
      <Skeleton width="55%" height={44} />
    ) : (
      <MDTypography variant="h3" fontWeight="bold" sx={{ letterSpacing: "-0.035em" }}>
        {count ?? "—"}
      </MDTypography>
    )}

    {percentage && (
      <MDTypography variant="caption" color="text" display="block" mt={1.25}>
        <MDTypography
          component="span"
          variant="caption"
          fontWeight="bold"
          color={percentage.color || "text"}
        >
          {percentage.amount ?? "—"}
        </MDTypography>{" "}
        {percentage.label}
      </MDTypography>
    )}
  </Card>
);

export default ComplexStatisticsCard;
